<template>
    <base-layers-tree-item :item="item">
        <div class="layer-row pl-2">
            <div class="grow-1 d-flex items-center pr-2">
                <div v-if="!flags?.isDesign && !flags?.readonly" class="d-flex items-center pr-3">
                    <div
                        v-if="canChangeElementVisibility"
                        class="action cursor-pointer no-drag"
                        @click.stop="onLayerVisibilityClick"
                    >
                        <font-awesome-icon
                            v-if="isVisible"
                            class="mr-1 action-icon"
                            :icon="['far', 'eye']"
                        />
                        <font-awesome-icon v-else class="mr-1 action-icon" :icon="['fas', 'eye-slash']" />
                    </div>

                    <div
                        v-if="canChangeElementLock"
                        class="action cursor-pointer no-drag"
                        @click.stop="onLayerLockClick"
                    >
                        <font-awesome-icon
                            v-if="isLocked || (!isPreviewStep && isElementSolidBackground)"
                            class="mr-1 action-icon"
                            :icon="['fas', 'lock']"
                        />
                        <font-awesome-icon v-else class="mr-1 action-icon" :icon="['far', 'lock-open']" />
                    </div>
                </div>
                <div class="d-flex pr-1 items-center w-full">
                    <div v-if="item.element.isOverlay" class="mr-2">
                        <font-awesome-icon class="mr-1 status-icon" :icon="['far', 'clone']" />
                    </div>
                    <div v-else-if="item.element.isPrintArea" class="mr-2">
                        <font-awesome-icon class="mr-1 status-icon" :icon="['far', 'print']" />
                    </div>
                    <div
                        v-else-if="
                            item.element.type === 'image-placeholder' &&
                            item.element.dynamicImagesPath &&
                            item.element.isColorOptions
                        "
                        class="mr-2"
                    >
                        <font-awesome-icon class="mr-1 status-icon" :icon="['far', 'palette']" />
                    </div>
                    <div v-else class="icon mr-2 justify-start">
                        <c-lazy class="d-flex items-center element-icon">
                            <svg-icon :src="getElementIcon" />
                        </c-lazy>
                    </div>

                    <div
                        v-if="item.element.type === 'bounded-text' && item.element.useAsMask"
                        class="icon mr-2 justify-start"
                    >
                        <font-awesome-icon class="d-flex items-center element-icon" :icon="['far', 'square-ring']" />
                    </div>

                    <div
                        v-if="
                            item.element.type === 'image-placeholder' &&
                            item.element.textMaskUuid &&
                            item.element.textMaskUuid.length > 1
                        "
                        class="icon mr-2 justify-start"
                    >
                        <font-awesome-icon
                            class="d-flex items-center element-icon"
                            :icon="['far', 'circle-half-stroke']"
                        />
                    </div>

                    <div
                        class="layers-element-title grow-1"
                        :class="{
                            'not-visible': !isVisible
                        }"
                    >
                        <editable-text
                            :value="item.element.name || ''"
                            :default-text="elementTitle"
                            :allow-editing="!flags?.readonly"
                            @input="onElementTitleChange"
                        />
                    </div>
                </div>
            </div>

            <div v-if="!flags?.readonly" class="layer-shrink text-center">
                <div class="user-select-none nowrap">ID {{ item.element.id }}</div>
            </div>
        </div>
    </base-layers-tree-item>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { getCanvasElementIcon, getCanvasElementTitle } from '@/utils/elementsUtils';
import {
    canChangeElementLock as _canChangeElementLock,
    canChangeElementVisibility as _canChangeElementVisibility,
    isElementSolidBackground as _isElementSolidBackground
} from '@/components/Layers/utils';

import SvgIcon from '@/components/SvgIcon/SvgIcon.vue';
import EditableText from '@/components/EditableText/EditableText.vue';
import CLazy from '@/components/CLazy.vue';
import BaseLayersTreeItem from '../BaseLayersTreeItem.vue';
import type { ListElements } from '@/typings/customilyElements/layer';
import { useLayerTreeItem } from '@/components/Layers/composables/useLayerTreeItem';

const props = defineProps<{
    item: ListElements;
}>();

const { flags, isVisible, isLocked, onElementTitleChange, onVisibilityClick, onLockClick } = useLayerTreeItem({
    item: props.item
});

const isPreviewStep = computed(() => {
    return flags.value?.isPreviewStep;
});

const canChangeElementLock = computed(() => _canChangeElementLock(props.item.element, isPreviewStep.value || false));

const canChangeElementVisibility = computed(() =>
    _canChangeElementVisibility(props.item.element, isPreviewStep.value || false)
);

const onLayerVisibilityClick = () => {
    onVisibilityClick();
};

const onLayerLockClick = () => {
    if (!flags.value?.isPreviewStep && _isElementSolidBackground(props.item.element)) {
        return;
    }
    onLockClick();
};

const isElementSolidBackground = computed(() => _isElementSolidBackground(props.item.element));

const elementTitle = computed(() => getCanvasElementTitle(props.item.element));

const getElementIcon = computed(() => getCanvasElementIcon(props.item.element));
</script>

<style lang="scss" scoped>
.nowrap {
    white-space: nowrap;
}
.action-icon {
    color: var(--secondary-hover-color);

    &.selected {
        color: #000;
    }
}
.status-icon {
    fill: var(--primary-blue);
    color: var(--primary-blue);

    &.selected {
        fill: #000;
        color: #000;
    }
}
.justify-start {
    justify-content: start !important;
}
</style>
